import type React from 'react';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';

/**
 * UI-tool registry — the host maps a tool NAME to either a React renderer or a
 * fire-once side-effect ("action"), and the library owns everything else:
 * routing the `uiTool` part kind here, parsing/unwrapping the wire args,
 * normalizing the connector-qualified function name, firing actions once per
 * tool-call id (and not on replay), and drawing an optional status chip.
 *
 * The per-entry generic (`render<TArgs>` / `action<TArgs>`) is a COMPILE-TIME
 * assertion only: args arrive off the wire as `unknown` (usually a JSON string,
 * sometimes double-encoded), so a missing field is `undefined` at runtime no
 * matter what the type claims. Pass `opts.parse` when you want a real runtime
 * guard/coercion.
 *
 * Build once at module load and pass the result as `<AiChatProvider uiTools={…} />`:
 *
 *     export const uiTools = createUiToolRegistry()
 *       .render<{ id: string; subAgentId?: string }>('present_dashboard',
 *           (a) => <ScriptDashboardViewer dashboardId={a.id} … />)
 *       .action<{ mode?: 'create' | 'update' }>('present_dashboard_pad',
 *           (a, { env }) => env.updateDashboardPad?.(JSON.parse(a.definitionJson)),
 *           { status: 'Rascunho atualizado' })
 *       .build();
 */

/** Default args shape when a registration omits its generic. */
export type UiToolArgs = Record<string, unknown>;

/** Context handed to every renderer. */
export interface UiToolContext {
    /** Normalized tool name (`UI_` marker + `{Plugin}-`/`{Plugin}_` qualifier stripped). */
    name: string;
    /** This tool call's id — stable across the call's Composing→Executing→Done lifecycle. */
    callId: string;
    /** The tool RESULT, if any. May be an object or a (possibly double-encoded) JSON string. */
    response: unknown;
    /**
     * The tool's CLIENT-ONLY output — the payload it wrote to its SDK `ToolClientOutput` sink,
     * already parsed. This is the preferred render source: it arrives correlated to THIS call (by
     * id) on the result event (so multiple UI calls never race), the LLM never saw it, and it
     * survives reload. `undefined` until the call has responded, or when the tool didn't opt in
     * (fall back to `args`). Replaces the old slot-id-correlated tool-event lookup over `parts`.
     */
    clientOutput: unknown;
    /** Sibling parts of the same interaction (e.g. present_tsx's compiled-JS event rides here). */
    parts: ReadonlyArray<InteractionPart>;
    /** The whole interaction this part belongs to. */
    message: Interaction;
    /** True while the turn is still streaming. */
    onGoing: boolean;
    /** True during historical replay / preview — actions must NOT re-fire side effects. */
    preview: boolean;
}

/** Context handed to actions — adds the host environment. */
export interface UiToolActionContext extends UiToolContext {
    /**
     * The host environment from `EnvironmentProvider` (e.g. `refreshDashboard`,
     * `createDashboardPad`). Empty `{}` when no provider is in scope or during
     * replay (`InertEnvironmentProvider` no-ops every callable) — so action calls
     * simply dead-end rather than throw.
     */
    env: Record<string, unknown>;
}

export type UiToolRenderFn<TArgs> = (args: TArgs, ctx: UiToolContext) => React.ReactNode;
export type UiToolActionFn<TArgs> = (args: TArgs, ctx: UiToolActionContext) => void | Promise<void>;
/**
 * The interactive face of an ASK-USER component — a human-in-the-loop prompt drawn in the docked
 * ask-user drawer (`AskUserSheet`) while the tool call is PARKED, keyed by the component NAME the
 * server stamps via `[KernelUIComponent]` (`functionDetails.askUserComponent`), not by the wire tool
 * name. Same `(args, ctx)` shape as a render tool.
 */
export type UiToolAskUserQuestionFn<TArgs> = (args: TArgs, ctx: UiToolContext) => React.ReactNode;

/**
 * The ANSWERED face of an ask-user component — the locked summary drawn inline in the transcript once
 * the tool has responded. Receives the tool's response (also available as `ctx.response`), so a host
 * can render "you answered X" without re-deriving it from args.
 */
export type UiToolAskUserResponseFn<TArgs> = (args: TArgs, ctx: UiToolContext, response: unknown) => React.ReactNode;

/**
 * A host ask-user registration: the interactive `question` (required, drawn while parked) plus an
 * optional locked `response` summary (drawn inline once answered). Omit `response` to reuse `question`
 * in both states — the question component then owns its own answered/locked view (as the built-in
 * `AskUserPart`/`AskAuthPart` do).
 */
export interface UiToolAskUserRenderers<TArgs> {
    question: UiToolAskUserQuestionFn<TArgs>;
    response?: UiToolAskUserResponseFn<TArgs>;
}

export interface UiToolRenderOptions<TArgs> {
    /** Runtime arg guard/coercion. Return `null` to render nothing (treated as invalid args). */
    parse?: (raw: unknown) => TArgs | null;
}

export interface UiToolActionOptions<TArgs> {
    /** Runtime arg guard/coercion. Return `null` to skip firing (treated as invalid args). */
    parse?: (raw: unknown) => TArgs | null;
    /**
     * Optional status chip shown AFTER the action fires: a string, or a function
     * of (args, ctx) returning the label (or `null` for no chip). Omit entirely
     * for a silent action — no UI at all, just the side effect.
     */
    status?: string | ((args: TArgs, ctx: UiToolActionContext) => string | null);
}

// --- stored (type-erased) entries -------------------------------------------
// The call-site generic erases to `any` here; safety lives at registration.

export interface UiToolRenderEntry {
    kind: 'render';
    name: string;
    render: UiToolRenderFn<any>;
    parse?: (raw: unknown) => any;
}
export interface UiToolActionEntry {
    kind: 'action';
    name: string;
    run: UiToolActionFn<any>;
    parse?: (raw: unknown) => any;
    status?: string | ((args: any, ctx: UiToolActionContext) => string | null);
}
export interface UiToolAskUserEntry {
    kind: 'askUser';
    name: string;
    question: UiToolAskUserQuestionFn<any>;
    response?: UiToolAskUserResponseFn<any>;
    parse?: (raw: unknown) => any;
}
export type UiToolEntry = UiToolRenderEntry | UiToolActionEntry | UiToolAskUserEntry;

/** A built, read-only registry passed to `<AiChatProvider uiTools={…} />`. */
export interface UiToolRegistry {
    /** Resolve the render/action entry for a RAW (possibly `UI_`-prefixed / connector-qualified) tool name. */
    resolve(rawName: string | undefined | null): UiToolEntry | undefined;
    /**
     * Resolve an ASK-USER component by its exact name (the `[KernelUIComponent]` value the server
     * stamps as `functionDetails.askUserComponent`). Exact match only — component names are authored
     * verbatim, not connector-qualified.
     */
    resolveAskUser(componentName: string | undefined | null): UiToolAskUserEntry | undefined;
    /** All registered (normalized) names. */
    names(): string[];
}

export interface UiToolRegistryBuilder {
    render<TArgs = UiToolArgs>(name: string, render: UiToolRenderFn<TArgs>, opts?: UiToolRenderOptions<TArgs>): UiToolRegistryBuilder;
    action<TArgs = UiToolArgs>(name: string, run: UiToolActionFn<TArgs>, opts?: UiToolActionOptions<TArgs>): UiToolRegistryBuilder;
    /**
     * Register an ASK-USER component drawn in the docked drawer (`AskUserSheet`). `componentName` must
     * match the server's `[KernelUIComponent("…")]` value (arrives as `functionDetails.askUserComponent`).
     * `AskUserPart` (questionnaire) and `AskAuthPart` (sign-in) are handled built-in by `AskUserDispatcher`
     * when unregistered, so consumers only register their own components (e.g. `AskCreateAccount`).
     * Pass `{ question, response? }`: `question` is the interactive parked form, `response` the optional
     * locked summary shown once answered.
     */
    askUser<TArgs = UiToolArgs>(componentName: string, renderers: UiToolAskUserRenderers<TArgs>, opts?: UiToolRenderOptions<TArgs>): UiToolRegistryBuilder;
    build(): UiToolRegistry;
}

/**
 * Strip the `UI_` render-tool marker wherever it sits — at the very start
 * (`UI_present_dashboard`) or right after a connector's plugin separator
 * (`MyPlugin-UI_present_dashboard` / `MyPlugin_UI_present_dashboard`).
 */
function stripUiMarker(raw: string): string {
    return raw.replace(/(^|[-_])UI_/, '$1');
}

/**
 * Tolerant unwrap for a tool's `clientOutput` payload. It reaches the client as a JSON string
 * (server-serialized) on the live path and as a string off the persisted sibling on replay; peel up
 * to three string layers (only when the trimmed text looks like JSON). Unlike {@link parseUiArgs}
 * this does NOT require an object — a tool may hand the client an array or primitive. Returns the
 * parsed value, the original non-string value as-is, or `undefined` when absent/unparseable.
 */
export function parseClientOutput(raw: unknown): unknown {
    let r: unknown = raw;
    if (r == null) return undefined;
    for (let i = 0; i < 3 && typeof r === 'string'; i++) {
        const t = r.trim();
        if (!(t.startsWith('{') || t.startsWith('[') || t.startsWith('"'))) break;
        try { r = JSON.parse(t); } catch { return undefined; }
    }
    return r;
}

/**
 * Tolerant arg unwrap. Args reach the client as an object on the live path, but as a
 * JSON string — occasionally double-encoded — on the persisted/replay path. Peel up to
 * three string layers (only when the trimmed text looks like JSON), then require an
 * object. Returns `null` when absent/unparseable; mirrors `InteractionPartUtils.uiSlotId`.
 */
export function parseUiArgs(raw: unknown): UiToolArgs | null {
    let r: unknown = raw;
    for (let i = 0; i < 3 && typeof r === 'string'; i++) {
        const t = r.trim();
        if (!(t.startsWith('{') || t.startsWith('['))) break;
        try { r = JSON.parse(t); } catch { return null; }
    }
    return r != null && typeof r === 'object' ? (r as UiToolArgs) : null;
}

export function createUiToolRegistry(): UiToolRegistryBuilder {
    // Render/action tools are keyed by (marker-stripped) WIRE name and resolved with connector-qualified
    // suffix matching. Ask-user components live in a SEPARATE namespace keyed by their verbatim
    // [KernelUIComponent] name (exact match) — the two never collide (snake_case vs PascalCase).
    const entries = new Map<string, UiToolEntry>();
    const askUserEntries = new Map<string, UiToolAskUserEntry>();

    const builder: UiToolRegistryBuilder = {
        render(name, render, opts) {
            const key = stripUiMarker(name);
            entries.set(key, { kind: 'render', name: key, render: render as UiToolRenderFn<any>, parse: opts?.parse });
            return builder;
        },
        action(name, run, opts) {
            const key = stripUiMarker(name);
            entries.set(key, {
                kind: 'action',
                name: key,
                run: run as UiToolActionFn<any>,
                parse: opts?.parse,
                status: opts?.status as UiToolActionEntry['status'],
            });
            return builder;
        },
        askUser(componentName, renderers, opts) {
            askUserEntries.set(componentName, {
                kind: 'askUser',
                name: componentName,
                question: renderers.question as UiToolAskUserQuestionFn<any>,
                response: renderers.response as UiToolAskUserResponseFn<any> | undefined,
                parse: opts?.parse,
            });
            return builder;
        },
        build() {
            // Snapshot so post-build mutation of the builder can't leak in.
            const frozen = new Map(entries);
            const frozenAskUser = new Map(askUserEntries);
            return {
                resolve(rawName) {
                    if (!rawName) return undefined;
                    const n = stripUiMarker(rawName);
                    const exact = frozen.get(n);
                    if (exact) return exact;
                    // Connector-qualified form ({plugin}-{name} / {plugin}_{name}). Match the
                    // longest registered name that `n` ends with so e.g. `present_chart` wins
                    // over a hypothetical `chart`. (Mirrors the isAskUser suffix matcher.)
                    let best: UiToolEntry | undefined;
                    let bestLen = -1;
                    for (const [name, entry] of frozen) {
                        if ((n.endsWith('-' + name) || n.endsWith('_' + name)) && name.length > bestLen) {
                            best = entry;
                            bestLen = name.length;
                        }
                    }
                    return best;
                },
                resolveAskUser(componentName) {
                    if (!componentName) return undefined;
                    return frozenAskUser.get(componentName);
                },
                names() {
                    return [...frozen.keys(), ...frozenAskUser.keys()];
                },
            };
        },
    };
    return builder;
}
